import { Box, Card, CardHeader, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import RadioButton from "@covertree/ui/buttons/RadioButton";
import { InfoDisclaimerBox } from "@covertree/ui/content";

import { POLICIES_PATH } from "@routing/paths";
import { SALES_EMAIL_ADDRESS } from "@covertree/consts";
import {
  isCreditCardPaymentMethod,
  mapAchPaymentMethodName,
  mapCreditCardPaymentMethodExpiration,
  mapCreditCardPaymentMethodName,
} from "../../../../PolicyDetailsContent/ContentTabs/Payment/utils";
import { PaymentMethodType } from "../types";

interface Props {
  readonly method: PaymentMethodType;
  readonly policyLocator: string;
  readonly isExpired?: boolean;
}

/** Default saved card on the endorsement checkout — read-only, with a link to the Payments tab. */
export function CardItem({ method, policyLocator, isExpired = false }: Props) {
  const name = isCreditCardPaymentMethod(method)
    ? mapCreditCardPaymentMethodName(method)
    : mapAchPaymentMethodName(method);

  const subtitle = isCreditCardPaymentMethod(method)
    ? mapCreditCardPaymentMethodExpiration(method)
    : undefined;

  return (
    <>
      <Box mt={3}>
        <Card
          data-testid={`endorsement-card-item-${method.id}`}
          sx={{
            borderColor: isExpired ? "action.disabled" : "primary.main",
            opacity: isExpired ? 0.5 : 1,
          }}>
          <Stack px={3} py={2} direction="row" spacing={2} alignItems="center">
            <RadioButton checked disabled={isExpired} />
            <Stack>
              <CardHeader sx={{ p: 0 }} title={name} />
              {subtitle && (
                <Typography variant="body2" color="text.disabled">
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Card>
      </Box>

      <InfoDisclaimerBox mt={3}>
        To add or change your payment method, visit the{" "}
        <Link
          color="secondary"
          component={RouterLink}
          to={`/${POLICIES_PATH}/${policyLocator}?tab=payment`}
          data-testid="endorsement-payments-tab-link">
          Payments tab
        </Link>{" "}
        on Policy Details. To switch to Mortgage Bill or for any other issues,
        contact{" "}
        <Link color="secondary" href={`mailto:${SALES_EMAIL_ADDRESS}`}>
          {SALES_EMAIL_ADDRESS}
        </Link>
      </InfoDisclaimerBox>
    </>
  );
}
